/* ==================================================================
   Product page — the text and markup half of the client's mockup.
   Companion to portafoto-pdp-mockup.css, which does all the styling.

   HOW THIS COEXISTS WITH portafoto-cro.js

   That file owns the variant label text. For every size label it does:

     const raw = input.value || ...;                  // the raw option value
     const formatted = formatVariantText(raw);        // "3 براويز • 40×60 • EGP 2100"
     if (visibleText.textContent.trim() !== formatted)
       visibleText.textContent = formatted;

   and it re-runs from a MutationObserver on .productView-product that
   never disconnects. An earlier version of this file rewrote that same
   .text node into a compact chip. The two then fought forever — every
   repair woke the other script, which undid it, which woke this one —
   and the size row visibly flickered.

   So this file no longer touches .text at all. It reads the same raw
   option value and renders a SEPARATE .pf-size-chip element beside it;
   the stylesheet clips .text (keeping it for screen readers, where the
   longer wording is actually better) and shows the chip. cro.js only
   ever writes to .text, so it now finds exactly what it expects, makes
   no change, and the loop cannot start.

   The other rules still hold:

     * every insertion is marked with a data-pf- attribute or a class and
       skipped if it is already there, so re-running is free
     * text is only written when it differs from what is on the node
     * work is scheduled on both requestAnimationFrame and a setTimeout
       fallback, because a backgrounded tab never fires rAF

   Nothing here gates the Add to cart button or the checkout link.
   ================================================================== */
(function () {
  'use strict';
  if (window.PortafotoLang === 'en') return;

  /* snippets/schema.liquid is rendered more than once on a product page,
     so this file is requested and executed twice. The insertions are all
     idempotent, but two MutationObservers on the buy area are pure waste
     — bail out of the second copy. */
  if (window.__portafotoPdpMockup) return;
  window.__portafotoPdpMockup = true;

  function isProductPage() {
    return !!(document.body && document.body.classList.contains('template-product'));
  }

  /* ---------------------------------------------------------------
     Copy, taken from the mockup the client supplied.
     --------------------------------------------------------------- */
  var TEXT = {
    badge: 'الأكثر طلباً',
    subtitle: 'تكوين جاهز ومتناسق يكمُل ديكور حائطك بسهولة.',
    sizeHeading: 'اختار المقاس المناسب',
    colorHeading: 'اختار لون الإطار',
    buyHeading: 'الكمية',
    qtyLabel: 'الكمية',
    pricePrefix: 'يبدأ من',
    currency: 'جنيه'
  };

  /* Shopify option values, verbatim: black, Wood, White, Gold, Dark Wood.
     Matched case-insensitively — the casing is inconsistent in the admin
     and must not be "tidied" there, because the theme matches variants on
     these strings. */
  var COLOR_AR = {
    black: 'أسود',
    white: 'أبيض',
    wood: 'خشبي',
    'natural wood': 'خشبي طبيعي',
    'dark wood': 'خشبي داكن',
    gold: 'ذهبي',
    golden: 'ذهبي',
    brown: 'بني',
    beige: 'بيج',
    grey: 'رمادي',
    gray: 'رمادي'
  };

  function colorName(value) {
    var raw = String(value || '').trim();
    return COLOR_AR[raw.toLowerCase().replace(/\s+/g, ' ')] || raw;
  }

  var CART_ICON =
    '<svg class="pf-cart-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M3 4h2.2l2.3 10.2a2 2 0 002 1.6h7.2a2 2 0 002-1.6L20 7H6"/>' +
    '<circle cx="9.5" cy="19.5" r="1.5"/><circle cx="17" cy="19.5" r="1.5"/></svg>';

  /* The first item said "شحن مجاني" in the client's mockup. Shipping is
     not free — the delivery profile charges 150 EGP in Cairo, 175 in the
     rest of Egypt and 250 for the Red Sea and South Sinai — so it states
     the starting rate instead. Do not put a claim in this row that the
     store cannot honour. */
  var TRUST = [
    ['شحن لكل مصر', 'يبدأ من 150 جنيه',
      '<path d="M3 7h11v8H3z"/><path d="M14 10h3.5l2.5 3v2H14z"/><circle cx="7" cy="17.5" r="1.6"/><circle cx="16.5" cy="17.5" r="1.6"/>'],
    ['ضمان سنتين', 'ضد عيوب التصنيع',
      '<path d="M12 3l7 3v5c0 4.2-2.9 7.7-7 9-4.1-1.3-7-4.8-7-9V6z"/><path d="M9 12l2 2 4-4"/>'],
    ['منتجات فاخرة', 'بجودة عالية',
      '<path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.2l5.9-.9z"/>'],
    ['دعم ومساعدة', 'عبر واتساب',
      '<path d="M4 12a8 8 0 1116 0v4a2 2 0 01-2 2h-2"/><rect x="3" y="11" width="3.5" height="6" rx="1.4"/><rect x="17.5" y="11" width="3.5" height="6" rx="1.4"/>']
  ];

  /* ---------------------------------------------------------------
     Small helpers.
     --------------------------------------------------------------- */

  /* Only ever write when the value actually changes — otherwise the
     MutationObserver re-triggers on our own writes, forever. */
  function setText(el, value) {
    /* v16: the locale bridge shows every price in one style ("2,100 ج.م").
       Write that same form here, or this script and the bridge would keep
       rewriting each other's price text forever. */
    if (typeof window.__pfMoney === 'function') value = window.__pfMoney(String(value));
    if (el && el.textContent !== value) el.textContent = value;
  }

  function setAttr(el, name, value) {
    if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function digitsOf(s) {
    var m = String(s).replace(/[٬,]/g, '').match(/\d+(?:\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  }

  function withCommas(n) {
    return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Pull the dimensions and the price out of whatever shape the option
     value is in:
       "3 frames 40x60 for 2100 EGP"          raw option value
       "3 frames size 40×60 price 2100 EGP"   raw option value
       "2 frames size 70×100"                 raw, no price
       "3 براويز • 40×60 • EGP 2100"          portafoto-cro.js

     The mockup writes the larger number first — "60 × 40", "90 × 60" —
     so the pair is ordered rather than passed through as written. */
  function parseSizeValue(raw) {
    if (!raw) return null;
    var text = String(raw);
    var dim = text.match(/(\d{2,3})\s*[x×X\*]\s*(\d{2,3})/);
    if (!dim) return null;

    var a = parseInt(dim[1], 10);
    var b = parseInt(dim[2], 10);
    var rest = text.slice(dim.index + dim[0].length);
    var price = rest.match(/(?:for|price|سعر|EGP|ج(?:نيه)?)\s*([\d,]{3,7})/i)
      || rest.match(/([\d,]{3,7})\s*(?:EGP|ج(?:نيه)?)/i);

    return {
      dim: Math.max(a, b) + ' × ' + Math.min(a, b),
      price: price ? digitsOf(price[1]) : null
    };
  }

  function optionValueOf(label) {
    var id = label.getAttribute('for');
    var input = id ? document.getElementById(id) : null;
    if (input && input.value) return input.value;
    var textEl = label.querySelector('.text');
    if (!textEl) return label.textContent;
    return textEl.getAttribute('data-portafoto-original-value') || textEl.textContent;
  }

  /* ---------------------------------------------------------------
     1. Badge and subtitle.
     --------------------------------------------------------------- */
  function buildHeader() {
    var badge = document.querySelector('.portafoto-growth-intro__badge');
    if (badge) setText(badge, TEXT.badge);

    var title = document.querySelector('.productView-title');
    if (!title || !title.parentElement) return;
    var sub = title.parentElement.querySelector('[data-pf-subtitle]');
    if (!sub) {
      sub = document.createElement('p');
      sub.className = 'pf-subtitle';
      sub.setAttribute('data-pf-subtitle', '');
      title.insertAdjacentElement('afterend', sub);
    }
    setText(sub, TEXT.subtitle);
  }

  /* ---------------------------------------------------------------
     2. Price: "LE 2,100.00" -> "2,100 جنيه", with a "يبدأ من" prefix
        shown only while the cheapest size is the one selected. Showing
        it over any other size would misstate the price.
     --------------------------------------------------------------- */
  function cheapestSizePrice() {
    var lowest = null;
    var labels = document.querySelectorAll('fieldset label.product-form__label');
    for (var i = 0; i < labels.length; i++) {
      var parsed = parseSizeValue(optionValueOf(labels[i]));
      if (parsed && parsed.price != null && (lowest === null || parsed.price < lowest)) {
        lowest = parsed.price;
      }
    }
    return lowest;
  }

  function formatPrice() {
    var wrap = document.querySelector('.productView-product .price');
    if (!wrap) return;

    var node = wrap.querySelector('.price__last .price-item--regular')
      || wrap.querySelector('.price-item--regular')
      || wrap.querySelector('.price-item');
    if (!node) return;

    var shown = digitsOf(node.textContent);
    if (shown === null) return;
    setText(node, withCommas(shown) + ' ' + TEXT.currency);

    var prefix = (wrap.parentElement || wrap).querySelector('[data-pf-price-prefix]');
    var lowest = cheapestSizePrice();
    var isCheapest = lowest !== null && Math.round(lowest) === Math.round(shown);

    if (isCheapest) {
      if (!prefix) {
        prefix = document.createElement('span');
        prefix.className = 'pf-price-prefix';
        prefix.setAttribute('data-pf-price-prefix', '');
        prefix.textContent = TEXT.pricePrefix;
        wrap.insertAdjacentElement('beforebegin', prefix);
      }
    } else if (prefix) {
      prefix.remove();
    }
  }

  /* ---------------------------------------------------------------
     3. Headings above each variant group, plus a rule under the price.

        The colour heading carries the selected colour after it, because
        the swatches are images with no text and nothing else on the page
        names the chosen frame colour. The theme's own legend does say it,
        but it is clipped: portafoto-pdp-cro.js stops watching after ten
        seconds, after which a colour change leaves the raw English value
        ("لون الإطار: black") on screen.
     --------------------------------------------------------------- */
  function addHeading(fieldset, copy, value) {
    if (!fieldset) return;
    /* Size and Colour are siblings inside one <variant-radios>, so this
       guard has to be per fieldset. Looking for the marker anywhere in
       the shared parent would let the size heading block the colour one. */
    var prev = fieldset.previousElementSibling;
    var h = prev && prev.hasAttribute && prev.hasAttribute('data-pf-sec-title') ? prev : null;

    if (!h) {
      h = document.createElement('div');
      h.className = 'pf-sec-title';
      h.setAttribute('data-pf-sec-title', '');
      h.setAttribute('aria-hidden', 'true');
      fieldset.insertAdjacentElement('beforebegin', h);
    }
    if (!h.querySelector('.pf-sec-title__label')) {
      h.innerHTML = '<span class="pf-sec-title__label"></span><span class="pf-sec-title__value"></span>';
    }
    setText(h.querySelector('.pf-sec-title__label'), copy);
    setText(h.querySelector('.pf-sec-title__value'), value || '');
  }

  function buildHeadings() {
    var sets = document.querySelectorAll('.productView-options fieldset');
    for (var i = 0; i < sets.length; i++) {
      var input = sets[i].querySelector('input[name]');
      var name = input ? input.getAttribute('name') : '';

      if (/^Size/i.test(name)) {
        addHeading(sets[i], TEXT.sizeHeading, '');
      } else if (/^Colou?r/i.test(name)) {
        var checked = sets[i].querySelector('input:checked');
        addHeading(sets[i], TEXT.colorHeading, checked ? colorName(checked.value) : '');
        nameSwatches(sets[i]);
      }
    }
  }

  /* The colour swatches are background images inside empty labels, so the
     radios have no accessible name at all — a screen reader announced
     five unnamed options. Naming the input is an attribute write, which
     neither this script's observer nor cro.js's reacts to, so it cannot
     start a rewrite loop the way touching the label would. */
  function nameSwatches(fieldset) {
    var inputs = fieldset.querySelectorAll('input[type="radio"]');
    for (var i = 0; i < inputs.length; i++) {
      setAttr(inputs[i], 'aria-label', colorName(inputs[i].value));
    }
  }

  /* When the theme re-renders the variant area it replaces the fieldset
     but leaves our heading behind, so a heading can end up above
     something that is not its group any more. Rather than delete it (the
     fieldset often returns a frame later), hide it whenever it is not
     immediately followed by a fieldset. */
  function pruneHeadings() {
    var heads = document.querySelectorAll('[data-pf-sec-title]');
    for (var i = 0; i < heads.length; i++) {
      var next = heads[i].nextElementSibling;
      var orphaned = !next || next.tagName !== 'FIELDSET';
      if (heads[i].hidden !== orphaned) heads[i].hidden = orphaned;
    }
  }

  function buildRule() {
    var price = document.querySelector('.productView-product .price');
    if (!price) return;
    var item = price.closest('.productView-moreItem') || price.parentElement;
    if (!item || !item.parentElement) return;
    if (item.parentElement.querySelector('[data-pf-rule]')) return;
    var hr = document.createElement('div');
    hr.className = 'pf-rule';
    hr.setAttribute('data-pf-rule', '');
    item.insertAdjacentElement('afterend', hr);
  }

  /* ---------------------------------------------------------------
     4. Buy row: a heading above the stepper and the button, and the
        cart icon inside the button. The row itself is the theme's own
        .pvGroup-row — see the note in the stylesheet about the page
        carrying two steppers, only one of which is inside the form.
     --------------------------------------------------------------- */
  function buildBuyRow() {
    var buttons = document.querySelector('.productView-buttons');
    if (buttons && buttons.parentElement) {
      var prev = buttons.previousElementSibling;
      var already = prev && prev.hasAttribute && prev.hasAttribute('data-pf-buy-heading');
      if (!already) {
        var h = document.createElement('div');
        h.className = 'pf-buy-heading';
        h.setAttribute('data-pf-buy-heading', '');
        h.setAttribute('aria-hidden', 'true');
        h.textContent = TEXT.buyHeading;
        buttons.insertAdjacentElement('beforebegin', h);
      }
    }

    /* The label stays in the accessibility tree; the stylesheet clips it. */
    var qtyLabel = document.querySelector('.productView-buttons .quantity__label');
    if (qtyLabel) setText(qtyLabel, TEXT.qtyLabel);

    var submit = document.querySelector('.product-form__submit');
    if (submit && !submit.querySelector('.pf-cart-icon')) {
      submit.insertAdjacentHTML('afterbegin', CART_ICON);
    }
  }

  /* ---------------------------------------------------------------
     5. Size chips.

        Rendered as a sibling of .text, never into it — see the note at
        the top of this file about the flicker that caused. The chip is
        created once per label and only its two text nodes are updated
        afterwards, so a settled page produces no mutations at all.
     --------------------------------------------------------------- */
  function buildChip(label, parsed) {
    // Trio pages own their size-and-price cards; do not add a second chip.
    if (label.closest('.pf-order-pilot')) {
      label.querySelector('.pf-size-chip')?.remove();
      return;
    }
    var chip = label.querySelector('.pf-size-chip');
    if (!chip) {
      chip = document.createElement('span');
      chip.className = 'pf-size-chip';
      chip.setAttribute('aria-hidden', 'true');
      chip.innerHTML = '<span class="pf-size-dim"></span><span class="pf-size-price"></span>';
      label.appendChild(chip);
    }
    setText(chip.querySelector('.pf-size-dim'), parsed.dim);
    setText(
      chip.querySelector('.pf-size-price'),
      parsed.price == null ? '' : withCommas(parsed.price) + ' ' + TEXT.currency
    );
  }

  function buildSizeChips() {
    var sets = document.querySelectorAll('fieldset');
    for (var i = 0; i < sets.length; i++) {
      var input = sets[i].querySelector('input[name]');
      if (!input || !/^Size/i.test(input.getAttribute('name') || '')) continue;

      var labels = sets[i].querySelectorAll('label.product-form__label');
      for (var j = 0; j < labels.length; j++) {
        var parsed = parseSizeValue(optionValueOf(labels[j]));
        if (parsed) buildChip(labels[j], parsed);
      }
    }
  }

  /* ---------------------------------------------------------------
     6. Trust row under the buy area.
     --------------------------------------------------------------- */
  function buildTrustRow() {
    var buttons = document.querySelector('.productView-buttons');
    if (!buttons) return;
    var item = buttons.closest('.productView-moreItem') || buttons;
    var parent = item.parentElement;
    if (!parent || parent.querySelector('[data-pf-trust]')) return;

    var ul = document.createElement('ul');
    ul.className = 'pf-trust';
    ul.setAttribute('data-pf-trust', '');
    for (var i = 0; i < TRUST.length; i++) {
      var li = document.createElement('li');
      li.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' + TRUST[i][2] + '</svg>' +
        '<span class="pf-trust__title"></span><span class="pf-trust__note"></span>';
      li.querySelector('.pf-trust__title').textContent = TRUST[i][0];
      li.querySelector('.pf-trust__note').textContent = TRUST[i][1];
      ul.appendChild(li);
    }
    item.insertAdjacentElement('afterend', ul);
  }

  /* ---------------------------------------------------------------
     Runner.
     --------------------------------------------------------------- */
  var running = false;

  function run() {
    if (running) return;
    running = true;
    try {
      buildSizeChips();
      buildHeader();
      buildHeadings();
      pruneHeadings();
      buildRule();
      formatPrice();
      buildBuyRow();
      buildTrustRow();
    } catch (err) {
      /* A broken decoration must never take the buy button down with it. */
      if (window.console && console.warn) console.warn('[portafoto pdp]', err);
    }
    running = false;
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    var fire = function () {
      if (!queued) return;
      queued = false;
      run();
    };
    /* rAF for the normal case, timeout because a backgrounded tab never
       fires rAF at all and the page would stay half-built. */
    requestAnimationFrame(fire);
    setTimeout(fire, 80);
  }

  /* childList only. characterData was tried and removed: cro.js rewrites
     .text by assigning textContent, and waking on that turned a race into
     a visible flicker. Now that nothing here writes to .text there is
     nothing to wake for — a settled page produces no mutations from
     either script.

     The timed passes cover the one case an observer cannot: markup that
     is replaced before the observer is attached. They stop after six
     seconds so nothing polls for the life of the page. */
  var SELF_HEAL_MS = [250, 700, 1500, 3000, 6000];

  function start() {
    run();
    var target = document.querySelector('.productView-product') || document.body;
    new MutationObserver(schedule).observe(target, { childList: true, subtree: true });
    document.addEventListener('variant:changed', schedule);
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name && /^(Size|Colou?r)/i.test(e.target.name)) schedule();
    });
    window.addEventListener('load', schedule);
    for (var i = 0; i < SELF_HEAL_MS.length; i++) setTimeout(run, SELF_HEAL_MS[i]);
  }

  function boot() {
    if (isProductPage()) start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
